import { createSelector } from '@reduxjs/toolkit';

import { EnvQuestionValue } from '@/domain/impact-env/entities/question.entity';
import { selectImpactEnvResponses } from '@/domain/impact-env/impact-env.selectors';
import { RootState } from '@/domain/store';

import { CalculateFestival } from './entities/calculate-festival.entity';
import {
  CalculateFestivalCo2,
  EMPTY_CALCULATE_FESTIVAL_CO2,
} from './entities/calculate-festival-co2.entity';
import {
  CalculateFestivalEnv,
  EMPTY_CALCULATE_FESTIVAL_ENV,
  VALUE_REF_KG_CO2_BY_PARTICIPANT,
} from './entities/calculate-festival-env.entity';
import {
  CalculateFestivalEss,
  EMPTY_CALCULATE_FESTIVAL_ESS,
} from './entities/calculate-festival-ess.entity';

export const selectImpactCalculateFestivalCo2ForProject =
  (projectId: number) =>
  (state: RootState): CalculateFestivalCo2 => {
    const calculate = selectImpactCalculateFestivalForProject(projectId)(state);
    if (calculate?.co2) {
      return calculate.co2;
    } else {
      return EMPTY_CALCULATE_FESTIVAL_CO2;
    }
  };

export const selectImpactCalculateFestivalEnvForProject =
  (projectId: number) =>
  (state: RootState): CalculateFestivalEnv => {
    const calculate = selectImpactCalculateFestivalForProject(projectId)(state);
    if (calculate?.env) {
      return calculate.env;
    } else {
      return EMPTY_CALCULATE_FESTIVAL_ENV;
    }
  };

export const selectImpactCalculateFestivalEssForProject =
  (projectId: number) =>
  (state: RootState): CalculateFestivalEss => {
    const calculate = selectImpactCalculateFestivalForProject(projectId)(state);
    if (calculate?.ess) {
      return calculate.ess;
    } else {
      return EMPTY_CALCULATE_FESTIVAL_ESS;
    }
  };

export const selectImpactCalculateFestivalForProject =
  (projectId: number) =>
  (state: RootState): CalculateFestival | null => {
    const calculate = state.impactCalculateFestival.byProjectId[projectId];
    if (calculate) {
      return calculate;
    } else {
      return null;
    }
  };

export const selectImpactCalculateFestivalEnvExtraValuesForCurrentProject =
  createSelector([selectImpactEnvResponses], (responsesEnv) => {
    const valueKgeCO2ByParticipant =
      computeValueKgeCO2ByParticipant(responsesEnv);
    const valueRefKgeCO2ByParticipant = VALUE_REF_KG_CO2_BY_PARTICIPANT;
    return {
      valueKgeCO2ByParticipant,
      valueRefKgeCO2ByParticipant,
    };
  });

function computeValueKgeCO2ByParticipant(responsesEnv: EnvQuestionValue[]) {
  const data = findDataToComputeValueKgeCO2ByParticipant(responsesEnv);
  if (!data) return 0;
  let result = data.co2GlobalScoreKg / data.numberOfParticipant;
  result = parseFloat(result.toFixed(2));
  return result;
}

function findDataToComputeValueKgeCO2ByParticipant(
  responsesEnv: EnvQuestionValue[],
) {
  const responseEnv0 = responsesEnv.find((x) => x.paramId === 'ENV_0');
  if (!responseEnv0) return;
  const factorRegex = /^(\d*\.?\d*)(?:##)?(\d*\.?\d*)$/;
  const values = factorRegex.exec(responseEnv0.value);
  if (!values) return;
  const globalScoreCo2 = values[1];
  const projectAvgCapacity = values[2];
  const globalScoreCo2Float = parseFloat(globalScoreCo2);
  const projectAvgCapacityNumber = parseInt(projectAvgCapacity);
  return {
    co2GlobalScoreKg: globalScoreCo2Float,
    numberOfParticipant: projectAvgCapacityNumber,
  };
}
